import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  TablePagination,
  Box,
  Typography,
  Tooltip,
  LinearProgress,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  AddCircle as AddCircleIcon,
  History as HistoryIcon,
  Update as UpdateIcon,
  DirectionsCar as DirectionsCarIcon,
  Diamond as DiamondIcon,
  Stars as StarsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import type { HostSubscriptionInfo } from '../types/subscription.types';
import { getDaysUntilExpiry, isExpiringSoon } from '../../../services/fleetSubscriptionService';

interface HostsTableProps {
  hosts: HostSubscriptionInfo[];
  isLoading?: boolean;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  selectedHosts: number[];
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSelectHost: (hostId: number) => void;
  onSelectAll: (checked: boolean) => void;
  onGrantSubscription: (host: HostSubscriptionInfo) => void;
  onViewDetails: (host: HostSubscriptionInfo) => void;
  onViewHistory: (host: HostSubscriptionInfo) => void;
}

const HostsTable: React.FC<HostsTableProps> = ({
  hosts,
  isLoading = false,
  page,
  rowsPerPage,
  totalCount,
  selectedHosts,
  onPageChange,
  onRowsPerPageChange,
  onSelectHost,
  onSelectAll,
  onGrantSubscription,
  onViewDetails,
  onViewHistory,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedHost, setSelectedHost] = useState<HostSubscriptionInfo | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, host: HostSubscriptionInfo) => {
    setAnchorEl(event.currentTarget);
    setSelectedHost(host);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedHost(null);
  };

  const getQualificationBadge = (qualificationPath: string) => {
    switch (qualificationPath) {
      case 'vehicles':
        return (
          <Chip
            icon={<DirectionsCarIcon />}
            label="5+ Vehicles"
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      case 'premium':
        return (
          <Chip
            icon={<DiamondIcon />}
            label="Premium"
            size="small"
            sx={{
              background: 'linear-gradient(45deg, #9333ea, #c084fc)',
              color: 'white',
              border: 'none',
            }}
          />
        );
      case 'both':
        return (
          <Chip
            icon={<StarsIcon />}
            label="Both"
            size="small"
            sx={{
              background: 'linear-gradient(45deg, #3b82f6, #9333ea)',
              color: 'white',
              border: 'none',
            }}
          />
        );
      default:
        return (
          <Chip
            label="None"
            size="small"
            variant="outlined"
            sx={{ color: 'text.secondary', borderColor: 'text.disabled' }}
          />
        );
    }
  };

  const getSubscriptionStatusChip = (subscription: HostSubscriptionInfo['active_subscription']) => {
    if (!subscription) {
      return <Chip label="No Subscription" size="small" variant="outlined" color="default" />;
    }

    if (subscription.status === 'active') {
      const daysLeft = getDaysUntilExpiry(subscription.end_date);
      const expiringSoon = isExpiringSoon(subscription.end_date);

      if (expiringSoon) {
        return (
          <Tooltip title={`Expires in ${daysLeft} days`}>
            <Chip
              icon={<ScheduleIcon />}
              label="Expiring Soon"
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 165, 0, 0.1)',
                color: '#FFA500',
                borderColor: '#FFA500',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                },
              }}
            />
          </Tooltip>
        );
      }

      return <Chip icon={<CheckCircleIcon />} label="Active" size="small" color="success" />;
    }

    return <Chip icon={<CancelIcon />} label="Expired" size="small" color="error" />;
  };

  const getHostTypeBadge = (hostType: string) => {
    if (hostType === 'fleet_manager') {
      return <Chip label="Fleet Manager" size="small" color="primary" />;
    }
    return <Chip label="Individual" size="small" variant="outlined" />;
  };

  const formatExpiryDate = (endDate: string) => {
    const date = new Date(endDate);
    const daysLeft = getDaysUntilExpiry(endDate);

    if (daysLeft < 0) {
      return (
        <Typography variant="body2" color="error">
          Expired {Math.abs(daysLeft)} days ago
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="body2">{date.toLocaleDateString()}</Typography>
        <Typography variant="caption" color="text.secondary">
          ({daysLeft} days left)
        </Typography>
      </Box>
    );
  };

  const allSelected = hosts.length > 0 && selectedHosts.length === hosts.length;
  const someSelected = selectedHosts.length > 0 && selectedHosts.length < hosts.length;

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {isLoading && <LinearProgress />}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Host</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Qualification</TableCell>
              <TableCell align="center">Vehicles</TableCell>
              <TableCell>Subscription Status</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hosts.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No hosts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              hosts.map((host) => (
                <TableRow
                  key={host.user_id}
                  hover
                  selected={selectedHosts.includes(host.user_id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedHosts.includes(host.user_id)}
                      onChange={() => onSelectHost(host.user_id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={host.profile_picture || undefined}
                        alt={host.full_name}
                        sx={{ width: 40, height: 40 }}
                      >
                        {host.full_name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {host.full_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {host.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{getHostTypeBadge(host.host_type)}</TableCell>
                  <TableCell>{getQualificationBadge(host.qualification_path)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={host.vehicle_count}
                      size="small"
                      color={host.vehicle_count >= 5 ? 'primary' : 'default'}
                      variant={host.vehicle_count >= 5 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{getSubscriptionStatusChip(host.active_subscription)}</TableCell>
                  <TableCell>
                    {host.active_subscription
                      ? formatExpiryDate(host.active_subscription.end_date)
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, host)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedHost) onViewDetails(selectedHost);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedHost) onGrantSubscription(selectedHost);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <AddCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Grant Subscription</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedHost) onViewHistory(selectedHost);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View History</ListItemText>
        </MenuItem>
        {selectedHost?.active_subscription?.status === 'active' && (
          <MenuItem
            onClick={() => {
              if (selectedHost) onGrantSubscription(selectedHost);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <UpdateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Extend Subscription</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
};

export default HostsTable;
